import type { ImageLoaderProps } from "next/dist/shared/lib/image-config";
import { IPageLink } from "@/sanity/globals/CTA";
import { IPopup } from "@/sanity/globals/Popups";
import type { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { PortableTextBlock } from "sanity";
import { II18nText, II18nRichtext } from "@/sanity/globals/I18n";

export interface GROQImage {
  src: string;
  width: number;
  height: number;
  lqip: string;
  name: string;
}

export interface GROQCTA {
  name: string;
  title?: string;
  titleI18n?: II18nText;
  icon?: string;
  action: "link" | "popup" | "anchor" | "videoPopup";
  link?: IPageLink;
  popup?: IPopup;
  anchor?: string;
  videoPopup?: IVideoPopup;
}

export interface IVideoPopup {
  video?: IMuxVideoPlayer;
  cta?: GROQCornerButton;
}

export interface GROQButton {
  type?:
    | "default"
    | "thickBorder"
    | "ghost"
    | "transparent"
    | "borders"
    | "darkGreen"
    | "lightGreen"
    | "white";
  size?: "sm" | "default" | "lg";
  shape?: "square" | "round" | "pill";
  cta?: GROQCTA;
}

export interface GROQCornerButton {
  type?: "default" | "dark" | "white" | "clearOutline" | "launchWhite" | "launchBlack";
  cta?: GROQCTA;
}

export interface IMenuSubItem {
  title: string;
  titleI18n?: II18nText;
  description?: string;
  descriptionI18n?: II18nText;
  icon?: GROQImage;
  link: IPageLink;
}

export interface IMenuSocialSubItem {
  image: GROQImage;
  title: string;
  link: IPageLink;
}

export interface IMenu {
  title: string;
  titleI18n?: II18nText;
  link?: IPageLink;
  isCategory: boolean;
  subItems?: IMenuSubItem[];
  socialSubItems?: IMenuSocialSubItem[];
}

export interface IHeader {
  name: string;
  menu: IMenu[];
  headerCTA: GROQButton;
}

export interface IFooter {
  name: string;
  impressumTitle: string;
  impressumTitleI18n?: II18nText;
  impressumLink: IPageLink;
  datenschutzTitle: string;
  datenschutzTitleI18n?: II18nText;
  datenschutzLink: IPageLink;
  communityTitle: string;
  communityTitleI18n?: II18nText;
  communityText: PortableTextBlock[];
  communityTextI18n?: II18nRichtext;
  communityLink: string;
  legalNotice?: PortableTextBlock[];
  legalNoticeI18n?: II18nRichtext;
  menu: {
    title: string;
    titleI18n?: II18nText;
    items?: {
      title: string;
      titleI18n?: II18nText;
      link: IPageLink;
    }[];
  }[];
}

export interface ITrustpilotTestimonial {
  name: string;
  testimonials: {
    image: GROQImage;
    name: string;
    position: string;
    text: PortableTextBlock[];
    textI18n?: II18nRichtext;
  }[];
}

export interface ITraders {
  name: string;
  items: {
    image: GROQImage;
    text: PortableTextBlock[];
    textI18n?: II18nRichtext[];
  }[];
}

export interface GROQMuxVideo {
  playbackId: string;
  assetId: string;
  filename: string;
  status: string;
}

export function GROQImageLoader({
  src,
  width,
  quality = 90,
}: ImageLoaderProps): string {
  return src + "?auto=format&q=" + quality;
}

export function GROQUnoptimizedImageLoader({
  src,
  width,
  quality = 75,
}: ImageLoaderProps): string {
  return src + "?q=100";
}

export const GROQImageSchema =
  '{"src": asset->url, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height, "lqip": asset->metadata.lqip, "name": asset->originalFilename}';

export const GROQPageLinkSchema =
  "{link,externalLink,openInNewTab,url,anchor,internalLink->{settings{slug{current},domain}}}";

export const HubSpotFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    submitTextI18n,
    message,
    messageI18n,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}
`;

export const ZapierFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    submitTextI18n,
    message,
    messageI18n,
    redirect${GROQPageLinkSchema},
    url,
  }
}
`;

export const GROQPopupSchemaQuery = `{
  name,
  theme,
  content,
  contentI18n,
  additionalContent,
  additionalContentI18n,
  formType,
  hsForm->${HubSpotFormSchema},
  zapierForm->${ZapierFormSchema},
}`;

export const GROQFormSchema = `{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    submitTextI18n,
    message,
    messageI18n,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}`;

export const GROQMuxVideoShema = `{
  "playbackId": asset->playbackId,
  "assetId": asset->assetId,
  "filename": asset->filename,
  "status": asset->status
}`;

export const GROQVideoPlayerSchema = `{
  sourceType,
  embed,
  muxVideo${GROQMuxVideoShema},
  embedUrl,
  htmlCode,
  thumbnail${GROQImageSchema}
}`;

const GROQPageCTAFields = `
  name,
  title,
  titleI18n,
  icon,
  action,
  anchor,
  link${GROQPageLinkSchema},
  popup->${GROQPopupSchemaQuery}
`;

const GROQVideoPopupSchema = `{
  video${GROQVideoPlayerSchema},
  cta{
    type,
    cta->{${GROQPageCTAFields}}
  }
}`;

export const GROQPageCTASchema = `{${GROQPageCTAFields},
  videoPopup${GROQVideoPopupSchema}
}`;

export const GROQButtonSchema = `{
  type,
  size,
  shape,
  cta->${GROQPageCTASchema}
}`;

export const GROQCornerButtonSchema = `{
  type,
  cta->${GROQPageCTASchema}
}`;

export const MuxVideoPlayerSchema = `{
  embed,
  muxVideo${GROQMuxVideoShema},
  embedUrl,
  thumbnail${GROQImageSchema}
}`;

export const HeaderSchema = `->{
  name,
  menu[]{
    title,
    titleI18n,
    link${GROQPageLinkSchema},
    isCategory,
    subItems[]{
      title,
      titleI18n,
      description,
      descriptionI18n,
      icon${GROQImageSchema},
      link${GROQPageLinkSchema},
    },
    socialSubItems[]{
      image${GROQImageSchema},
      title,
      link${GROQPageLinkSchema},
    },
  },
  headerCTA${GROQButtonSchema},
}`;

export const FooterSchema = `->{
  name,
  impressumTitle,
  impressumTitleI18n,
  impressumLink${GROQPageLinkSchema},
  datenschutzTitle,
  datenschutzTitleI18n,
  datenschutzLink${GROQPageLinkSchema},
  communityTitle,
  communityTitleI18n,
  communityText,
  communityTextI18n,
  communityLink,
  legalNotice,
  legalNoticeI18n,
  menu[]{
    title,
    titleI18n,
    items[]{
      title,
      titleI18n,
      link${GROQPageLinkSchema},
    },
  },
}`;

export const TrustpilotTestimonialsSchema = `{
  name,
  testimonials[]{
    image${GROQImageSchema},
    name,
    position,
    text,
    textI18n,
  },
}`;

export const TradersSchema = `{
  name,
  items[]{
    image${GROQImageSchema},
    text,
    textI18n,
  },
}`;

export interface IAbTestVariant {
  _key?: string;
  variantKey: string;
  page: {
    _id: string;
    _type: string;
  };
}

export interface IAbTest {
  enabled?: boolean;
  posthogFlagKey?: string;
  variants?: IAbTestVariant[];
}

export const GROQAbTestSchema = `abTest{
  enabled,
  posthogFlagKey,
  variants[]{
    _key,
    variantKey,
    "page": page->{ _id, _type },
  },
}`;

